/**
 * The decision card's parts that need neither the agent's store nor its socket: its copy, terms,
 * reasons and the column's buttons. The chat's DecisionCard and the MCP view (ui://steddy/decision,
 * built on its own, `npm run build:mcp-view`) both draw from them.
 */
import { Banknote, ClipboardCheck, type LucideIcon } from "lucide-react";

import type {
  DecisionAction,
  DecisionKind,
  DecisionOutcome,
} from "@/components/underwriting/DecisionColumn";
import OfferCalculator from "@/components/underwriting/OfferCalculator";
import { Badge } from "@/components/ui/badge";

import { choiceOptions, isDeclineOption, optionSendsContract } from "../../choices/wire";
import type { WireChoice, WireChoiceOption } from "../../protocol/protocol";
import { CHANGE_HINT, EDIT_HINT, SectionLabel } from "./CardParts";
import type { OfferEdit } from "./overrides";
import { calculatorTerms, type DecisionCardPayload, type DecisionTool } from "./payloads";

export interface Copy {
  title: string;
  icon: LucideIcon;
  kind: DecisionKind;
  /** The column's block once the call ran. */
  outcome: DecisionOutcome;
  done: string;
  notDone: string;
  /** How to change what the card proposes, while it waits. */
  hint: string;
  /** The same, when the user may change it on the card. */
  editHint: string;
}

export const COPY: Record<DecisionTool, Copy> = {
  approve_application: {
    title: "Underwriting decision",
    icon: ClipboardCheck,
    kind: "underwriting",
    outcome: "approved",
    done: "Approved",
    notDone: "Not approved",
    hint: CHANGE_HINT,
    editHint: EDIT_HINT,
  },
  decline_application: {
    title: "Underwriting decision",
    icon: ClipboardCheck,
    kind: "underwriting",
    outcome: "declined",
    done: "Declined",
    notDone: "Not declined",
    hint: "Ask the assistant to change the reasons.",
    editHint: "Change the reasons as you decline, or ask the assistant.",
  },
  fund_application: {
    title: "Funding decision",
    icon: Banknote,
    kind: "funding",
    outcome: "funded",
    done: "Funded",
    notDone: "Not funded",
    hint: CHANGE_HINT,
    editHint: CHANGE_HINT,
  },
};

/**
 * The Offer Builder at the card's terms, keyed to them (no draft, no org default): read-only, or
 * the user's to change with `edit`, each term checked against an approve's limits (`onProblems`
 * hears what is wrong with them).
 */
export function Terms({
  card,
  edit,
  onProblems,
}: {
  card: DecisionCardPayload;
  edit?: OfferEdit;
  onProblems?: (problems: string[]) => void;
}) {
  if (!card.terms) return <p className="text-sm text-muted-foreground">No offer terms yet.</p>;
  return (
    <OfferCalculator
      key={`${JSON.stringify(card.terms)}:${edit?.key ?? ""}`}
      inline
      readOnly={!edit}
      persistDraft={false}
      currentLeveragePct={card.strip?.leverage?.percent ?? 0}
      averageRevenue={card.strip?.average_revenue ?? 0}
      initialValues={calculatorTerms(card.terms)}
      onOfferChange={edit?.onOffer}
      onLastEditedChange={edit?.onLastEdited}
      checkTerms={!!edit}
      onProblemsChange={edit ? onProblems : undefined}
    />
  );
}

export function Note({ card }: { card: DecisionCardPayload }) {
  if (!card.note?.text) return null;
  return (
    <div className="mt-3">
      <SectionLabel>
        {card.kind === "decline_application" ? "Additional information" : "Note"}
      </SectionLabel>
      <p className="whitespace-pre-wrap text-sm">{card.note.text}</p>
    </div>
  );
}

export function Reasons({ card }: { card: DecisionCardPayload }) {
  return (
    <div>
      <SectionLabel>Decline reasons</SectionLabel>
      <div className="flex flex-wrap gap-1.5">
        {card.reasons.map((reason) => (
          <Badge key={reason} variant="secondary" className="font-medium">
            {reason}
          </Badge>
        ))}
      </div>
      <Note card={card} />
    </div>
  );
}

/**
 * The approve variants' ids (`approval_variants`), for a choice stored before its options kept
 * their `overrides`: what each sends.
 */
const VARIANT_SENDS: Record<string, boolean> = {
  approve_send_contract: true,
  approve_without_contract: false,
};

const sendsContract = (option: WireChoiceOption) =>
  optionSendsContract(option) ?? VARIANT_SENDS[option.id] ?? null;

/**
 * The options the web card shows. The server's contract send (an option that sets send_contract)
 * is never shown or pressed: the web sends the contract itself once the approve ran (the dialog's
 * Approve + Send Contract, with its preview), so nothing sends twice. On a call that already sends,
 * its approve would send as well: its plain approve (send_contract false) stands in for it.
 */
export function webOptions(choice: WireChoice) {
  const options = choiceOptions(choice);
  const sends = options.some((option) => sendsContract(option) === false);
  return options.filter(
    (option) => sendsContract(option) !== true && !(sends && option.id === "approve")
  );
}

/**
 * The choice's own options as the column's buttons ("Fund $35,500" / "Don't fund"), each
 * pressing (or opening the dialog for) its own option; `approveOff`, all but the deny wait (the
 * card's terms are not ones an approve accepts).
 */
export function choiceActions(
  choice: WireChoice,
  onApprove: (option: string) => void,
  onDeny: (option: string) => void,
  pressed: string | null,
  approveOff = false
): DecisionAction[] {
  return webOptions(choice).map((option) =>
    isDeclineOption(option.id)
      ? {
          label: option.label,
          tone: "quiet",
          onClick: () => onDeny(option.id),
          disabled: pressed !== null,
        }
      : {
          label: option.label,
          tone: option.style === "danger" ? "decline" : "approve",
          onClick: () => onApprove(option.id),
          disabled: pressed !== null || approveOff,
        }
  );
}

/** What the column shows for a choice that is not waiting: why nothing is there to press. */
export function idleText(copy: Copy, choice: WireChoice | null, failed: boolean): string {
  if (failed) return "Didn't go through";
  if (choice?.status === "decided" && !isDeclineOption(choice.chosen)) return "Saving…";
  if (choice?.status === "decided") return copy.notDone;
  return "No decision";
}
